import type { TMenuLinks } from '@/components/Header/types';
import type {
    TypeButtonWithoutUnresolvableLinksResponse,
    TypeMegaMenuComponentWithoutUnresolvableLinksResponse,
    TypeMenuColumnWithoutUnresolvableLinksResponse,
} from '../_generated';
import type { TEntriesResponse } from '../types/types';
import path from 'path';
import { getLocalizedLink } from '@/utils/getLocalizedLink';

export const getMenuUrl = (
    lang: string,
    link: string,
    target: TypeButtonWithoutUnresolvableLinksResponse['fields']['target'],
) => {
    if (target !== '_blank') {
        return getLocalizedLink(link, lang);
    }
    return link;
};

export const normalizeMegaMenu = (
    lang: string,
    megaMenu?: TEntriesResponse<TypeMegaMenuComponentWithoutUnresolvableLinksResponse[]>,
): { headerId: string; headerLinks: TMenuLinks[] } => {
    const links: TMenuLinks[] = [];

    if (!megaMenu || !megaMenu.items) {
        return { headerId: '', headerLinks: [] };
    }

    megaMenu.items[0].fields.columns.forEach((item) => {
        if (!item) {
            return;
        }

        switch (item.sys.contentType.sys.id) {
            case 'button':
                links.push({
                    title: item.fields.title,
                    href: getMenuUrl(
                        lang,
                        (item as TypeButtonWithoutUnresolvableLinksResponse).fields.link ?? '/',
                        (item as TypeButtonWithoutUnresolvableLinksResponse).fields.target,
                    ),
                    id: item.sys.id,
                    target: (item as TypeButtonWithoutUnresolvableLinksResponse).fields.target,
                });
                break;
            case 'menuColumn':
                const isChildrenColumns = (
                    item as TypeMenuColumnWithoutUnresolvableLinksResponse
                ).fields.items.some((item) => item?.sys.contentType.sys.id === 'menuColumn');
                links.push({
                    title: item.fields.title,
                    href: '/',
                    id: item.sys.id,
                    subMenu: isChildrenColumns
                        ? (item as TypeMenuColumnWithoutUnresolvableLinksResponse).fields.items.map(
                              (column) => {
                                  if (!column) {
                                      return [];
                                  }

                                  return (
                                      column as TypeMenuColumnWithoutUnresolvableLinksResponse
                                  ).fields.items.map((item, index) => {
                                      return {
                                          title: item?.fields.title ?? '',
                                          href: getMenuUrl(
                                              lang,
                                              (item as TypeButtonWithoutUnresolvableLinksResponse)
                                                  .fields.link ?? '/',
                                              (item as TypeButtonWithoutUnresolvableLinksResponse)
                                                  .fields.target,
                                          ),
                                          id: item?.sys.id ?? '',
                                          target: (
                                              item as TypeButtonWithoutUnresolvableLinksResponse
                                          ).fields.target,
                                          parentId: column.sys.id,
                                          isHeader: index === 0,
                                      };
                                  });
                              },
                          )
                        : [
                              (
                                  item as TypeMenuColumnWithoutUnresolvableLinksResponse
                              ).fields.items.map((item) => {
                                  if (!item) {
                                      return { title: '', href: '', id: '' };
                                  }

                                  return {
                                      title: item.fields.title,
                                      href: getMenuUrl(
                                          lang,
                                          (item as TypeButtonWithoutUnresolvableLinksResponse)
                                              .fields.link ?? '/',
                                          (item as TypeButtonWithoutUnresolvableLinksResponse)
                                              .fields.target,
                                      ),
                                      target: (item as TypeButtonWithoutUnresolvableLinksResponse)
                                          .fields.target,
                                      id: item.sys.id,
                                  };
                              }),
                          ],
                });
                break;
        }
    });

    return { headerId: megaMenu.items[0].sys.id, headerLinks: links };
};
